import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Search } from "lucide-react";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  formatFinancialYearLabel,
  getFinancialYearDropdownOptions,
  readStoredFinancialYearStartYear,
} from "../utils/financialYear";
import {
  loadGroupsForCommissionSettings,
  displayGroupCommissionPercent,
} from "../services/groupEmployeeCommissionService";
import {
  listPartiesWithCommissionSettings,
  loadPartyGroupCommissionRateMap,
  savePartyGroupCommissionPercents,
} from "../services/partyEmployeeCommissionService";

/**
 * Settings: select FY + party → set discount % per group
 * → Employee sales report (same FY) uses these as commission.
 */
export default function GroupCommissionSettingsPage() {
  const [fyStartYear, setFyStartYear] = useState(() => readStoredFinancialYearStartYear());
  const [groups, setGroups] = useState([]);
  const [partyNames, setPartyNames] = useState([]);
  const [partyInput, setPartyInput] = useState("");
  const [selectedParty, setSelectedParty] = useState("");
  /** @type {Record<string, string>} groupId → draft % */
  const [drafts, setDrafts] = useState({});
  /** original rates when party loaded */
  const [savedRates, setSavedRates] = useState({});
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingParty, setLoadingParty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [queryText, setQueryText] = useState("");

  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fyOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingGroups(true);
      try {
        const [gList, pList] = await Promise.all([
          loadGroupsForCommissionSettings(),
          listPartiesWithCommissionSettings(),
        ]);
        if (cancelled) return;
        setGroups(gList);
        setPartyNames(pList);
      } catch (e) {
        console.error(e);
        if (!cancelled) toast.error(e?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPartyRates = useCallback(
    async (partyName, fy) => {
      const name = String(partyName || "").trim();
      const year = fy != null ? fy : fyStartYear;
      if (!name) {
        setSelectedParty("");
        setDrafts({});
        setSavedRates({});
        return;
      }
      setLoadingParty(true);
      setSelectedParty(name);
      try {
        const pack = await loadPartyGroupCommissionRateMap(name, year);
        const next = {};
        const orig = {};
        for (const g of groups) {
          let pct = null;
          if (pack.byGroupId.has(g.id)) pct = pack.byGroupId.get(g.id);
          else if (pack.byGroupName.has(String(g.name || "").toLowerCase())) {
            pct = pack.byGroupName.get(String(g.name || "").toLowerCase());
          }
          if (pct == null) pct = 0;
          next[g.id] = String(pct);
          orig[g.id] = Number(pct);
        }
        setDrafts(next);
        setSavedRates(orig);
        if (!partyNames.includes(name) && pack.rows.length) {
          setPartyNames((prev) => [...new Set([...prev, name])].sort((a, b) => a.localeCompare(b)));
        }
        if (pack.rows.length === 0) {
          toast(
            `No saved rates for “${name}” in FY ${formatFinancialYearLabel(year)} — set % and Save`,
            { duration: 3500 }
          );
        }
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "Failed to load party rates");
      } finally {
        setLoadingParty(false);
      }
    },
    [groups, partyNames, fyStartYear]
  );

  const applyParty = () => {
    const name = partyInput.trim();
    if (!name) {
      toast.error("Select or type a party / employee name");
      return;
    }
    loadPartyRates(name, fyStartYear);
  };

  // Re-load when FY changes and a party is selected
  useEffect(() => {
    if (!selectedParty) return;
    loadPartyRates(selectedParty, fyStartYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on FY change
  }, [fyStartYear]);

  const filtered = useMemo(() => {
    const q = queryText.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, queryText]);

  const dirtyCount = useMemo(() => {
    if (!selectedParty) return 0;
    let n = 0;
    for (const g of groups) {
      const orig = Number(savedRates[g.id] ?? 0);
      const cur = Number(drafts[g.id]);
      if (!Number.isFinite(cur)) n += 1;
      else if (Math.abs(cur - orig) > 0.0001) n += 1;
    }
    return n;
  }, [groups, drafts, savedRates, selectedParty]);

  const positiveRateCount = useMemo(() => {
    if (!selectedParty) return 0;
    return groups.filter((g) => Number(drafts[g.id]) > 0).length;
  }, [groups, drafts, selectedParty]);

  const setDraft = (id, value) => {
    setDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const handleSaveAll = async () => {
    if (!selectedParty) {
      toast.error("Select a party first");
      return;
    }
    /** @type {{ groupId: string, groupName: string, percent: number }[]} */
    const rows = [];
    for (const g of groups) {
      const cur = Number(drafts[g.id]);
      if (!Number.isFinite(cur) || cur < 0 || cur > 100) {
        toast.error(`Invalid % for “${g.name}” (use 0–100)`);
        return;
      }
      const orig = Number(savedRates[g.id] ?? 0);
      if (Math.abs(cur - orig) > 0.0001 || cur > 0) {
        rows.push({ groupId: g.id, groupName: g.name, percent: cur });
      }
    }
    if (!rows.length) {
      toast("No rates to save — set discount % greater than 0");
      return;
    }
    setSaving(true);
    try {
      await savePartyGroupCommissionPercents(selectedParty, rows, fyStartYear);
      toast.success(`Saved ${rows.length} group rate(s) for ${selectedParty} · FY ${fyLabel}`);
      setPartyNames((prev) =>
        [...new Set([...prev, selectedParty])].sort((a, b) => a.localeCompare(b))
      );
      await loadPartyRates(selectedParty, fyStartYear);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const fillFromGroupDefaults = () => {
    if (!selectedParty) return;
    const next = { ...drafts };
    for (const g of groups) {
      next[g.id] = String(displayGroupCommissionPercent(g));
    }
    setDrafts(next);
    toast("Filled from group defaults — click Save to store for this FY");
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="w-full shadow z-50">
        <MenuBar />
      </div>
      <div className="w-full border-b shadow-sm z-40">
        <HeaderBar />
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-slate-800">Party discount / commission</h1>
              <p className="text-sm text-slate-500 mt-1 max-w-xl">
                <strong>1.</strong> Select <strong>FY</strong> (e.g. 2025-26) → <strong>2.</strong> party →{" "}
                <strong>3.</strong> set discount % per group → Save. Same FY on{" "}
                <Link to="/reports/party-brand-monthly" className="text-indigo-600 hover:underline">
                  Employee sales
                </Link>{" "}
                shows commission = sale × % ÷ 100.
              </p>
            </div>
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={saving || loadingParty || !selectedParty || (dirtyCount === 0 && positiveRateCount === 0)}
              className={`px-4 py-2 rounded-md text-sm font-medium shadow-sm ${
                saving || !selectedParty || (dirtyCount === 0 && positiveRateCount === 0)
                  ? "bg-gray-200 text-slate-500 cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-700"
              }`}
            >
              {saving
                ? "Saving…"
                : dirtyCount
                  ? `Save ${dirtyCount} change(s) · ${fyLabel}`
                  : positiveRateCount
                    ? `Save for FY ${fyLabel}`
                    : "Saved"}
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Financial year</label>
              <select
                className="border rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-300"
                value={fyStartYear}
                onChange={(e) => setFyStartYear(Number(e.target.value))}
                disabled={loadingGroups || saving}
              >
                {fyOptions.map(({ startYear, label }) => (
                  <option key={startYear} value={startYear}>
                    {label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Rates for FY <strong>{fyLabel}</strong> apply on Employee sales when that FY is selected.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Party / employee</label>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  className="flex-1 min-w-[12rem] border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300"
                  list="party-commission-party-list"
                  value={partyInput}
                  onChange={(e) => setPartyInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyParty()}
                  placeholder="Same name as in Employee sales / sales import…"
                  disabled={loadingGroups}
                />
                <datalist id="party-commission-party-list">
                  {partyNames.map((p) => (
                    <option key={p} value={p} />
                  ))}
                </datalist>
                <button
                  type="button"
                  onClick={applyParty}
                  disabled={loadingGroups || !partyInput.trim()}
                  className="px-4 py-2 rounded-md bg-slate-800 text-white text-sm hover:bg-slate-900 disabled:opacity-50"
                >
                  Load party
                </button>
              </div>
              {selectedParty ? (
                <p className="text-xs text-emerald-700 mt-2">
                  Editing rates for <strong>{selectedParty}</strong> · FY <strong>{fyLabel}</strong>
                  {loadingParty ? " …" : ""}
                </p>
              ) : (
                <p className="text-xs text-slate-400 mt-2">Select FY + party, then set group discount %.</p>
              )}
            </div>
          </div>

          {!selectedParty ? (
            <div className="bg-white rounded-lg border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">
              Choose FY and party above to set group-wise discount (commission rates).
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b bg-slate-50">
                <div className="relative flex-1 max-w-sm">
                  <input
                    value={queryText}
                    onChange={(e) => setQueryText(e.target.value)}
                    placeholder="Search groups…"
                    className="pl-9 pr-3 py-2 border rounded-md w-full bg-white text-sm focus:ring-2 focus:ring-indigo-300"
                  />
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                </div>
                <button
                  type="button"
                  onClick={fillFromGroupDefaults}
                  className="text-xs text-indigo-600 hover:underline"
                >
                  Fill from group defaults
                </button>
                <span className="text-xs text-slate-500 whitespace-nowrap">
                  {filtered.length} / {groups.length} groups · FY {fyLabel}
                </span>
              </div>

              {loadingGroups || loadingParty ? (
                <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">
                  No groups. Create groups in{" "}
                  <Link to="/group-master" className="text-indigo-600 hover:underline">
                    Group Master
                  </Link>
                  .
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-600 border-b bg-slate-50">
                        <th className="py-2.5 px-4 font-medium">Group</th>
                        <th className="py-2.5 px-4 font-medium text-right w-40">Discount % (commission)</th>
                        <th className="py-2.5 px-4 font-medium text-right w-36 text-slate-400">
                          Example on ₹1L
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((g) => {
                        const pct = Number(drafts[g.id]);
                        const example = Number.isFinite(pct) ? (100000 * pct) / 100 : 0;
                        return (
                          <tr key={g.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                            <td className="py-2.5 px-4 font-medium text-slate-800">{g.name}</td>
                            <td className="py-2.5 px-4 text-right">
                              <input
                                type="number"
                                min={0}
                                max={100}
                                step={0.01}
                                value={drafts[g.id] ?? ""}
                                onChange={(e) => setDraft(g.id, e.target.value)}
                                className="w-28 border rounded-md px-2 py-1.5 text-right tabular-nums focus:ring-2 focus:ring-indigo-300"
                              />
                            </td>
                            <td className="py-2.5 px-4 text-right text-slate-500 tabular-nums text-xs">
                              ₹{" "}
                              {example.toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-slate-400 mt-4">
            Party name must match Employee sales. After save for FY {fyLabel}, open Employee sales with the{" "}
            <strong>same FY and party</strong> and click Show report — % Comm and Commission columns use these rates.
            Older rates (saved before FY was required) still apply as fallback.
          </p>
        </div>
      </div>
    </div>
  );
}
